import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Users, AlertTriangle, Filter, Search, Pill, TrendingUp, ChevronRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { differenceInYears, parseISO } from "date-fns";
import { cn } from "../lib/utils";
import { StatCard, Card, Badge, Button, ProgressBar } from "../components/ui";
import { AlertFeed } from "../components/alerts/AlertFeed";
import { PatientTable } from "../components/patients/PatientTable";
import { alertsApi, patientsApi, hospitalsApi, aiApi } from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { mockAlerts, mockPatients, mockPriorityPatients, mockStats } from "../lib/mock";

const adherencePlaceholder = [
  { day: "จ", rate: 82 }, { day: "อ", rate: 90 }, { day: "พ", rate: 75 },
  { day: "พฤ", rate: 88 }, { day: "ศ", rate: 92 }, { day: "ส", rate: 78 }, { day: "อา", rate: 85 },
];

const riskDisplay: Record<"low" | "medium" | "high" | "critical", { label: string; variant: "green" | "orange" | "red" }> = {
  low:      { label: "ต่ำ",      variant: "green"  },
  medium:   { label: "ปานกลาง", variant: "orange" },
  high:     { label: "สูง",      variant: "red"    },
  critical: { label: "วิกฤต",   variant: "red"    },
};

const riskBarColor: Record<"low" | "medium" | "high" | "critical", string> = {
  low:      "bg-green-500",
  medium:   "bg-orange-400",
  high:     "bg-red-500",
  critical: "bg-red-600",
};

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState<"alerts" | "patients">("alerts");
  const user = useAuthStore((s) => s.user);
  const qc   = useQueryClient();

  const { data: statsData } = useQuery({
    queryKey: ["stats", user?.hospital_id],
    queryFn: () => hospitalsApi.stats(user!.hospital_id!).then(r => r.data),
    enabled: !!user?.hospital_id,
    refetchInterval: 30_000,
  });

  const { data: alertsData } = useQuery({
    queryKey: ["alerts"],
    queryFn: () => alertsApi.list({ limit: 50 }).then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: patientsData } = useQuery({
    queryKey: ["patients"],
    queryFn: () => patientsApi.list({ limit: 100 }).then(r => r.data),
    refetchInterval: 60_000,
  });

  const { data: aiBriefingResponse, isFetching: aiLoading } = useQuery({
    queryKey: ["ai-briefing", user?.hospital_id],
    queryFn: () => aiApi.briefing().then(r => r.data),
    refetchInterval: 60_000,
  });

  const ackMutation = useMutation({
    mutationFn: (id: string) => alertsApi.acknowledge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });

  const rawAlerts = (alertsData?.data ?? []).map(a => ({
    id: a.id,
    patientName: (a as any).patient_name ?? "ผู้ป่วย",
    age: (a as any).patient_age ?? 0,
    title: a.notes ?? "แจ้งเตือนการรับยา",
    time: new Date(a.created_at),
    level: (a.level ?? "level1_patient") as "level1_patient" | "level2_caregiver" | "level3_hospital",
    status: (a.status ?? "pending") as "pending" | "escalated" | "acknowledged",
    medication: "", phone: "",
  }));

  const rawPatients = (patientsData?.data ?? []).map(p => ({
    id: p.id,
    name: (p as any).full_name ?? `HN-${p.hn}`,
    age: p.date_of_birth ? differenceInYears(new Date(), parseISO(p.date_of_birth)) : 0,
    hn: p.hn,
    diseases: p.chronic_diseases ?? [],
    adherence: (p as any).adherence_rate ?? 0,
    status: ((p as any).status ?? "ok") as "ok" | "alert" | "missed",
    lastSeen: new Date(p.created_at),
  }));

  const alerts   = rawAlerts.length   ? rawAlerts   : mockAlerts;
  const patients = rawPatients.length ? rawPatients : mockPatients;

  const aiBriefing       = aiBriefingResponse?.data;
  const rawPriority      = aiBriefing?.priority_patients.slice(0, 3) ?? [];
  const priorityPatients = rawPriority.length ? rawPriority : mockPriorityPatients;

  const totalPatients    = statsData?.total_patients ?? (rawPatients.length ? patientsData?.total : mockStats.total_patients) ?? mockStats.total_patients;
  const pendingAlerts    = rawAlerts.length ? (statsData?.pending_alerts ?? 0) : mockStats.pending_alerts;
  const totalAlertsToday = rawAlerts.length ? (alertsData?.total ?? 0) : mockStats.total_alerts_today;

  return (
    <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
      {/* ── Stat Cards ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="ผู้ป่วยทั้งหมด" value={totalPatients} delay={0}
          icon={<Users className="w-5 h-5 text-blue-600" />} iconBg="bg-blue-50 dark:bg-blue-900/30"
          subtitle="ในความดูแลของ รพสต."
          trend={{ value: 2, label: "จากเดือนที่แล้ว" }} />
        <StatCard title="แจ้งเตือนวันนี้" value={totalAlertsToday} delay={0.08}
          icon={<Bell className="w-5 h-5 text-orange-500" />} iconBg="bg-orange-50 dark:bg-orange-900/30"
          subtitle={`${pendingAlerts} รอดำเนินการ`}
          trend={{ value: -5, label: "จากเมื่อวาน" }} />
        <StatCard title="อัตราการรับยา" value="83%" delay={0.16}
          icon={<Pill className="w-5 h-5 text-brand-600" />} iconBg="bg-brand-50 dark:bg-brand-900/20"
          subtitle="เฉลี่ย 7 วันที่ผ่านมา"
          trend={{ value: 3, label: "จากสัปดาห์ก่อน" }} />
        <StatCard title="รอดำเนินการ" value={pendingAlerts} delay={0.24}
          icon={<AlertTriangle className="w-5 h-5 text-red-500" />} iconBg="bg-red-50 dark:bg-red-900/30"
          subtitle={pendingAlerts > 0 ? "ต้องดำเนินการด่วน" : "ทุกอย่างเรียบร้อย"} />
      </div>

      {/* ── Chart + AI Briefing ─────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Adherence chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="xl:col-span-2"
        >
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">อัตราการรับยา (รายวัน)</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">7 วันที่ผ่านมา</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="green" pulse>อัปเดตล่าสุด</Badge>
                <TrendingUp className="w-4 h-4 text-green-500" />
              </div>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={adherencePlaceholder}>
                <defs>
                  <linearGradient id="adherenceGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#16a34a" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(156,163,175,0.2)" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#9ca3af", fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis domain={[0, 100]} unit="%" tick={{ fill: "#9ca3af", fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: "12px", border: "1px solid rgba(229,231,235,0.5)", background: "rgba(255,255,255,0.95)", backdropFilter: "blur(8px)" }}
                  formatter={(v) => [`${v}%`, "อัตราการรับยา"]} />
                <Area type="monotone" dataKey="rate" stroke="#16a34a" strokeWidth={2.5} fill="url(#adherenceGrad)"
                  dot={{ fill: "#16a34a", strokeWidth: 2, r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </motion.div>

        {/* Risk Prioritization */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">จัดลำดับความเสี่ยง / Risk Prioritization</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {aiLoading ? "กำลังโหลด..." : (aiBriefing?.summary ?? "ประเมินความเสี่ยงล่าสุด")}
                </p>
              </div>
              <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 shrink-0">
                <TrendingUp className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
            </div>

            {/* Patient list */}
            <div className="flex-1 space-y-3">
              {priorityPatients.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 px-4 py-8 text-center"
                >
                  <TrendingUp className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">ยังไม่พบผู้ป่วยเสี่ยงเด่น</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">ระบบจะอัปเดตเมื่อมี alert หรือประวัติใหม่</p>
                </motion.div>
              ) : priorityPatients.map((patient, i) => {
                const risk = riskDisplay[patient.risk_level];
                const barColor = riskBarColor[patient.risk_level];
                return (
                  <motion.div
                    key={patient.patient_id}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3 hover:border-indigo-200 dark:hover:border-indigo-700 hover:bg-indigo-50/30 dark:hover:bg-indigo-900/10 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {patient.hn ? `HN-${patient.hn}` : patient.patient_id.slice(0, 8)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{patient.summary}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Badge variant={risk.variant}>{risk.label}</Badge>
                        <ChevronRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 group-hover:text-indigo-400 transition-colors" />
                      </div>
                    </div>
                    <ProgressBar value={patient.risk_score} color={barColor} delay={0.6 + i * 0.1} />
                    {patient.recommended_actions[0] && (
                      <div className="mt-2">
                        <p className="text-[11px] text-indigo-600 dark:text-indigo-400 truncate font-medium">
                          {patient.recommended_actions[0].label}
                        </p>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* ── Alerts / Patients Tab ──────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <Card padding="none">
          <div className="flex items-center justify-between px-6 pt-5 pb-0 border-b border-gray-100 dark:border-gray-700">
            <div className="flex gap-1">
              {(["alerts", "patients"] as const).map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={cn(
                    "px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors relative",
                    activeTab === tab
                      ? "text-brand-600 dark:text-brand-400 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-brand-600 dark:after:bg-brand-400"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  )}>
                  {tab === "alerts" ? (
                    <span className="flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      การแจ้งเตือน
                      {pendingAlerts > 0 && (
                        <span className="bg-red-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                          {pendingAlerts}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      รายชื่อผู้ป่วย
                    </span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 pb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input placeholder="ค้นหา..."
                  className="pl-9 pr-4 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 w-52 transition-all" />
              </div>
              <Button variant="outline" size="sm" icon={<Filter className="w-3.5 h-3.5" />}>กรอง</Button>
            </div>
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={activeTab}
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}>
              {activeTab === "alerts"
                ? <AlertFeed alerts={alerts} onAcknowledge={(id) => ackMutation.mutate(id)} />
                : <PatientTable patients={patients} />}
            </motion.div>
          </AnimatePresence>
        </Card>
      </motion.div>
    </div>
  );
}
